import { useState } from 'react';
import { Button, Modal } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useReconcileUserAutoJoin } from '@/hooks/useAutoJoinRules';
import { describeReconcileOutcome } from '@/lib/auto-join-conditions';
import { displayName } from '@/lib/user-display';
import { UserPicker } from './UserPicker';
import type { SpaceMembership, UserSummary } from '@/types/spaces';

interface AddViaRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  /** Active members — excluded from the picker. */
  existingMembers: SpaceMembership[];
}

/**
 * Re-runs the space's auto-join rules for a chosen user. The rule-correct way
 * to onboard someone a missed event skipped: the resulting membership is
 * `source: 'auto'` (so a future team-switch still auto-removes them), unlike a
 * manual add. Only joins the user if they actually match a rule.
 */
export function AddViaRulesModal({ isOpen, onClose, spaceId, existingMembers }: AddViaRulesModalProps) {
  const { success, info, error: showError } = useToast();
  const reconcile = useReconcileUserAutoJoin(spaceId);
  const [user, setUser] = useState<UserSummary | undefined>(undefined);

  const exclude = new Set(existingMembers.map((m) => m.userId));

  const handleClose = () => {
    if (reconcile.isPending) return;
    setUser(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    if (!user) return;
    try {
      const result = await reconcile.mutateAsync(user.userId);
      const { variant, message } = describeReconcileOutcome(result, displayName(user));
      if (variant === 'success') success(message);
      else info(message);
      setUser(undefined);
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to run auto-join rules');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add via auto-join rules"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={reconcile.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={reconcile.isPending} disabled={!user}>
            Run rules
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">User</label>
          <UserPicker
            value={user?.userId}
            onChange={setUser}
            excludeUserIds={exclude}
            placeholder="Search by email or name…"
          />
          <p className="text-xs text-muted-foreground">
            Re-runs this space's auto-join rules for the selected user. They're added
            (as a rule-managed member) only if they match a rule — so future attribute
            changes still keep their membership in sync. Use "Add manually" instead to
            add someone who doesn't match any rule.
          </p>
        </div>
      </div>
    </Modal>
  );
}
